import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { ClientTenantFullDetails } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/api/configuration.api.ts';
import { getApiClientTenantByAliasFullDetails } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/api/configuration.api.ts';
import {
    persistAdminCenterStepAsPublished,
    readAdminCenterPublishedSteps,
} from '@store/services/localStorage.service.ts';
import { extractError } from '@utils/slice.ts';

export interface ClientInfoError {
    message?: string;
    [k: string]: any;
}

export interface ClientInfoState {
    isLoading: boolean;
    error: ClientInfoError | null;
    publishedSteps: Record<string, boolean>;
    tenantInfoFullDetails: ClientTenantFullDetails | null;
    isGetTenantInfoFullDetailsPending: boolean;
    isAddNewTenantContext: boolean;
}

const initialState: ClientInfoState = {
    isLoading: false,
    error: null,
    publishedSteps: readAdminCenterPublishedSteps(),
    tenantInfoFullDetails: null,
    isGetTenantInfoFullDetailsPending: false,
    isAddNewTenantContext: false,
};

export const getTenantInfoByAliasFullDetails = createAsyncThunk<
    ClientTenantFullDetails,
    { clientId: number; alias: string },
    { rejectValue: ClientInfoError }
>('configuration/getTenantInfoByAliasFullDetails', ({ clientId, alias }, { rejectWithValue }) =>
    getApiClientTenantByAliasFullDetails(clientId, alias)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const configurationSlice = createSlice({
    name: 'configuration',
    initialState,
    reducers: {
        setPublishedSteps(state, action: PayloadAction<string>) {
            if (action.payload) {
                state.publishedSteps[action.payload] = true;
                persistAdminCenterStepAsPublished(action.payload);
            } else {
                state.publishedSteps = {};
            }
        },
        setTenantInfoFullDetails(state, action: PayloadAction<ClientTenantFullDetails | null>) {
            state.tenantInfoFullDetails = action.payload;
        },
        setIsAddNewTenantContext(state, action: PayloadAction<boolean>) {
            state.isAddNewTenantContext = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getTenantInfoByAliasFullDetails.pending, (state) => {
            state.isGetTenantInfoFullDetailsPending = true;
            state.error = null;
        });
        builder.addCase(getTenantInfoByAliasFullDetails.fulfilled, (state, action) => {
            state.isGetTenantInfoFullDetailsPending = false;
            state.tenantInfoFullDetails = action.payload;
        });
        builder.addCase(getTenantInfoByAliasFullDetails.rejected, (state, action) => {
            state.isGetTenantInfoFullDetailsPending = false;
            state.error = extractError(action);
        });
    },
});

export const { setPublishedSteps, setTenantInfoFullDetails, setIsAddNewTenantContext } = configurationSlice.actions;
export default configurationSlice.reducer;
